import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Switch, Drawer, message, Select, DatePicker, Tooltip } from 'antd';
import {
	ExportOutlined,
	RedoOutlined,
	PlusOutlined,
	EyeOutlined,
	CloseOutlined,
} from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import { RCTable, ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import { searchDriversV2, exportDriversCSVV2, updateDriverStatusV2 } from 'store/driverSliceV2';
import { usePersistedState } from 'hooks';
import { searchVendorV2 } from 'store/vendorSliceV2';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// FilterTag will use ant-tag class to match old UI

const FilterHighlightContainer = styled(Box)`
	flex: 0 0 45%;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

// Format date as dd-mm-yy (matching image format)
const formatDateShort = (date) => {
	if (!date) return '-';
	return dayjs(date).format('DD-MM-YY');
};

// Format date and time as dd-mm-yy, hh:mm (matching image format)
const formatDateTime = (dateTime) => {
	if (!dateTime) return '-';
	const date = dayjs(dateTime).format('DD-MM-YY');
	const time = dayjs(dateTime).format('HH:mm');
	return `${date}, ${time}`;
};

const mapVendorToOption = (vendor) => {
	const vendorId = vendor?.id ?? vendor?.vendor_id ?? vendor?.value;
	const vendorName = vendor?.vendor_name ?? vendor?.name ?? vendor?.label ?? `Vendor ${vendorId}`;

	return {
		value: vendorId,
		label: vendorName,
	};
};

const mergeVendorOptions = (...vendorLists) => {
	const optionsMap = new Map();

	vendorLists
		.flat()
		.filter(Boolean)
		.map(mapVendorToOption)
		.forEach((vendor) => {
			if (vendor.value !== null && vendor.value !== '') {
				optionsMap.set(vendor.value, vendor);
			}
		});

	return Array.from(optionsMap.values()).sort((firstVendor, secondVendor) =>
		firstVendor.label.localeCompare(secondVendor.label)
	);
};

const getTableColumns = (navigate, handleStatusChange) => [
	{
		title: 'Driver Name',
		dataIndex: 'driver_name',
		key: 'driver_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vendor Name',
		dataIndex: 'vendor_name',
		key: 'vendor_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Mobile Number',
		dataIndex: 'mobile_no',
		key: 'mobile_no',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Username',
		dataIndex: 'username',
		key: 'username',
		hidden: false,
		render: (username) => <Text overFlow="hidden">{username || ''}</Text>,
	},
	{
		title: 'City',
		dataIndex: 'city',
		key: 'city',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'DL proof',
		dataIndex: 'dl_proof',
		key: 'dl_proof',
		render: (text) => <Text>{formatDateShort(text)}</Text>,
	},
	{
		title: 'Multiple Trips',
		dataIndex: 'multiple_trips',
		key: 'multiple_trips',
		render: (value) => <Text>{value ? 'Yes' : 'No'}</Text>,
	},
	{
		title: 'Created at',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (text) => <Text>{formatDateTime(text)}</Text>,
	},
	{
		title: 'Created by',
		dataIndex: 'created_by_name',
		key: 'created_by_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (status, record) => (
			<Switch
				checked={status}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				onChange={(checked) => handleStatusChange(record, checked)}
			/>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id) => (
			<ViewButton onClick={() => navigate(`/drivers/${id}`)}>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];

export default function DriverListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchInput, setSearchInput] = usePersistedState('driverList:searchInput', ''); // Input field value
	const [searchValue, setSearchValue] = usePersistedState('driverList:search', ''); // Applied search value
	const [appliedFilters, setAppliedFilters] = usePersistedState('driverList:filters', {});
	const [rowData, setRowData] = useState({ rows: [], count: 0 });
	const [vendorOptions, setVendorOptions] = useState([]);
	// const [cityOptions, setCityOptions] = useState([]);
	const vendorSearchTimeoutRef = useRef(null);
	const [paginateConfig, setPaginateConfig] = usePersistedState('driverList:paginate', {
		search_type: 'driver',
		limit: 20,
		offset: 0,
		currentPage: 1,
	});

	const fetchVendorOptions = useCallback(
		async (searchTerm = '', selectedVendorId = null) => {
			try {
				const requests = [
					dispatch(
						searchVendorV2({
							filter_by: searchTerm ? { query_str: searchTerm } : {},
							pagination: { page: 1, per_page: 1000 },
						})
					),
				];

				if (selectedVendorId) {
					requests.push(
						dispatch(
							searchVendorV2({
								filter_by: { id: Number(selectedVendorId) },
								pagination: { page: 1, per_page: 1000 },
							})
						)
					);
				}

				const [searchResponse, selectedVendorResponse] = await Promise.all(requests);
				setVendorOptions(
					mergeVendorOptions(
						searchResponse?.rows || [],
						selectedVendorResponse?.rows || []
					)
				);
			} catch (error) {
				console.error('Error fetching vendors:', error);
				message.error('Failed to fetch vendors');
			}
		},
		[dispatch]
	);

	// Helper: Fetch filter options for dropdowns
	const fetchFilterOptions = useCallback(async () => {
		// Fetch cities from driver search API
		await dispatch(
			searchDriversV2({
				filter_by: {},
				offset: 0,
				limit: 1,
			})
		);

		if (vendorOptions.length === 0) {
			await fetchVendorOptions('', appliedFilters.vendor_id);
		}
	}, [appliedFilters.vendor_id, dispatch, fetchVendorOptions, vendorOptions.length]);

	// Populate filter form when drawer opens
	useEffect(() => {
		if (filterDrawerOpen) {
			fetchFilterOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen, appliedFilters, dispatch, fetchFilterOptions]);

	useEffect(
		() => () => {
			if (vendorSearchTimeoutRef.current) {
				clearTimeout(vendorSearchTimeoutRef.current);
			}
		},
		[]
	);

	const handleVendorSearch = useCallback(
		(value) => {
			if (vendorSearchTimeoutRef.current) {
				clearTimeout(vendorSearchTimeoutRef.current);
			}

			vendorSearchTimeoutRef.current = setTimeout(() => {
				fetchVendorOptions(value, appliedFilters.vendor_id);
			}, 300);
		},
		[fetchVendorOptions, appliedFilters.vendor_id]
	);

	const handleSearch = (value) => {
		const trimmedValue = value ? value.trim() : '';
		// Update both the input value and the applied search value
		setSearchInput(trimmedValue);
		setSearchValue(trimmedValue);
		// Reset to first page when searching
		setPaginateConfig((prev) => ({
			...prev,
			offset: 0,
			currentPage: 1,
		}));
	};

	const handleExport = async () => {
		try {
			const filters = {
				...(searchValue ? { driver_name: searchValue } : {}),
				...appliedFilters,
			};
			await dispatch(exportDriversCSVV2({ filter_by: filters }));
		} catch (error) {
			console.error('Export error:', error);
			message.error('Failed to export drivers');
		}
	};

	const handleSync = async () => {
		try {
			const response = await dispatch(
				searchDriversV2({
					filter_by: {
						...(searchValue ? { driver_name: searchValue } : {}),
						...appliedFilters,
					},
					offset: paginateConfig.offset || 0,
					limit: paginateConfig.limit || 20,
					sort_column: paginateConfig.sort_column,
					sort_direction: paginateConfig.sort_direction,
				})
			);

			if (response && response.rows) {
				setRowData(response);
				setPaginateConfig((prev) => ({
					...prev,
					count: response.count || 0,
				}));
				message.success('Driver list refreshed successfully');
			}
		} catch (error) {
			console.error('Sync error:', error);
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to refresh driver list';
			message.error(errorMessage);
		}
	};

	const handleCreate = () => {
		navigate('/drivers/add');
	};

	const handleStatusChange = useCallback(
		async (record, checked) => {
			try {
				const response = await dispatch(
					updateDriverStatusV2({ id: record.id, is_active: checked })
				);
				if (response?.status === 'success') {
					// Update local state immediately for better UX
					setRowData((prev) => ({
						...prev,
						rows: prev.rows.map((row) =>
							row.id === record.id ? { ...row, is_active: checked } : row
						),
					}));
				}
			} catch (error) {
				console.error('Status change error:', error);
				message.error('Failed to update driver status');
			}
		},
		[dispatch]
	);

	const handleResetFilters = () => {
		setAppliedFilters({});
		setFilterDrawerOpen(false);
		// Reset to first page when resetting filters
		setPaginateConfig((prev) => ({
			...prev,
			offset: 0,
			currentPage: 1,
		}));
	};

	// Remove individual filter
	const handleRemoveFilter = (filterKey) => {
		const newFilters = { ...appliedFilters };

		// Handle date range removal (both dates need to be removed)
		if (filterKey === 'dateRange') {
			delete newFilters.created_at_from;
			delete newFilters.created_at_to;
		} else {
			delete newFilters[filterKey];
		}

		setAppliedFilters(newFilters);

		// Reset to first page when removing filter
		setPaginateConfig((prev) => ({
			...prev,
			offset: 0,
			currentPage: 1,
		}));
	};

	// Helper: Build status filter display
	const buildStatusFilter = useCallback(() => {
		if (appliedFilters.is_active === undefined) return null;
		return {
			key: 'is_active',
			label: 'Status',
		};
	}, [appliedFilters.is_active]);

	// Helper: Build vendor filter display
	const buildVendorFilter = useCallback(() => {
		if (!appliedFilters.vendor_id) return null;
		return {
			key: 'vendor_id',
			label: 'Vendor',
		};
	}, [appliedFilters.vendor_id]);

	// Helper: Build city filter display
	// const buildCityFilter = useCallback(() => {
	// 	if (!appliedFilters.city) return null;
	// 	return {
	// 		key: 'city',
	// 		label: 'City',
	// 	};
	// }, [appliedFilters.city]);

	// Helper: Build date range filter display
	const buildDateRangeFilter = useCallback(() => {
		if (!appliedFilters.created_at_from && !appliedFilters.created_at_to) return null;
		return {
			key: 'dateRange',
			label: 'Created Date',
		};
	}, [appliedFilters.created_at_from, appliedFilters.created_at_to]);

	// Get applied filters display
	const getAppliedFiltersDisplay = useCallback(
		() =>
			[
				buildStatusFilter(),
				buildVendorFilter(),
				// buildCityFilter(),
				buildDateRangeFilter(),
			].filter(Boolean),
		[buildStatusFilter, buildVendorFilter, buildDateRangeFilter]
	);

	// Dispatch action for RCTable
	// RCTable expects a function that returns a thunk: (params) => async () => { ... }
	const dispatchAction = useCallback(
		(params) => {
			const { offset, limit, sort_column, sort_direction, ...rest } = params;
			const filters = {
				...(searchValue ? { driver_name: searchValue } : {}),
				...appliedFilters,
				...rest,
			};

			// Return the thunk function directly (RCTable will dispatch it)
			return searchDriversV2({
				filter_by: filters,
				offset: Number(offset) || 0,
				limit: Number(limit) || 20,
				sort_column,
				sort_direction,
			});
		},
		[searchValue, appliedFilters]
	);

	// Memoize table columns to prevent unnecessary re-renders
	const tableColumns = useMemo(
		() => getTableColumns(navigate, handleStatusChange),
		[navigate, handleStatusChange]
	);

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
						/>
					</Box>
					<FilterHighlightContainer>
						<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
							FILTERS
						</StyledFilterButton>
						<FilterDisplayBox>
							{getAppliedFiltersDisplay().map((filter) => (
								<FilterTag
									key={filter.key}
									onClose={() => {
										handleRemoveFilter(filter.key);
									}}
								>
									{filter.label}
								</FilterTag>
							))}
							{getAppliedFiltersDisplay().length > 0 && (
								<Tooltip title="Clear all filters">
									<CloseOutlined
										style={{
											color: '#ff4d4f',
											cursor: 'pointer',
											marginLeft: 'auto',
											marginRight: '4px',
										}}
										onClick={handleResetFilters}
									/>
								</Tooltip>
							)}
						</FilterDisplayBox>
					</FilterHighlightContainer>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					togglePaginate={[paginateConfig, setPaginateConfig]}
					dispatchAction={dispatchAction}
					columns={tableColumns}
					hasPagination
					configs={{
						staticPayload: {},
					}}
					pagination={{
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} drivers`,
					}}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setFilterDrawerOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px" display="block">
							Status
						</Text>
						<Select
							placeholder="Select status"
							allowClear
							size="large"
							style={{ width: '100%' }}
							value={(() => {
								if (appliedFilters.is_active === undefined) return undefined;
								return appliedFilters.is_active ? 'true' : 'false';
							})()}
							onChange={(val) => {
								setAppliedFilters((prev) => ({
									...prev,
									is_active: val === undefined ? undefined : val === 'true',
								}));
								setPaginateConfig((prev) => ({
									...prev,
									offset: 0,
									currentPage: 1,
								}));
							}}
						>
							<Option value="true">Active</Option>
							<Option value="false">Inactive</Option>
						</Select>
					</Box>

					<Box mb="2rem">
						<Text mb="5px" display="block">
							Vendor
						</Text>
						<Select
							placeholder="Select vendor"
							allowClear
							size="large"
							showSearch
							style={{ width: '100%' }}
							value={appliedFilters.vendor_id}
							filterOption={false}
							onSearch={handleVendorSearch}
							onDropdownVisibleChange={(open) => {
								if (open && vendorOptions.length === 0) {
									fetchVendorOptions('', appliedFilters.vendor_id);
								}
							}}
							onClear={() => {
								fetchVendorOptions('', null);
							}}
							onChange={(val) => {
								setAppliedFilters((prev) => ({
									...prev,
									vendor_id: val,
								}));
								setPaginateConfig((prev) => ({
									...prev,
									offset: 0,
									currentPage: 1,
								}));
							}}
						>
							{vendorOptions.map((vendor) => (
								<Option key={vendor.value} value={vendor.value}>
									{vendor.label}
								</Option>
							))}
						</Select>
					</Box>

					<Box mb="2rem">
						<Text mb="5px" display="block">
							Created Date
						</Text>
						<RangePicker
							style={{ width: '100%' }}
							size="large"
							format="DD-MM-YYYY"
							placeholder={['Start Date', 'End Date']}
							value={
								appliedFilters.created_at_from && appliedFilters.created_at_to
									? [
											dayjs(appliedFilters.created_at_from),
											dayjs(appliedFilters.created_at_to),
									  ]
									: null
							}
							onChange={(dates) => {
								setAppliedFilters((prev) => {
									const newFilters = { ...prev };
									if (dates && dates.length === 2 && dates[0] && dates[1]) {
										newFilters.created_at_from = dates[0]
											.startOf('day')
											.toISOString();
										newFilters.created_at_to = dates[1]
											.endOf('day')
											.toISOString();
									} else {
										delete newFilters.created_at_from;
										delete newFilters.created_at_to;
									}
									return newFilters;
								});
								setPaginateConfig((prev) => ({
									...prev,
									offset: 0,
									currentPage: 1,
								}));
							}}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={handleResetFilters}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
		</Box>
	);
}
